import React, { useState, useEffect, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import Tooltip from '@mui/material/Tooltip'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { toast } from 'react-toastify'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { getCurrentUserRole, selectHasAnyTaskManagementBp, selectFeatureVisibility, selectFeatureDefaultOwner } from 'features/auth/ducks/selectors'
import { isFeatureVisible } from 'constants/featureGroups'
import { selectIsOnline } from 'ducks/offline/selectors'
import { isOnline as checkIsOnline } from 'ducks/offline/actions'
import deferIdle from 'utils/deferIdle'
import { fetchTutorialsRoutine } from 'features/tutorials/ducks/actions'
import { selectTutorials } from 'features/tutorials/ducks/selectors'
import { getWhatsNewRoutine } from 'features/whatsNew/ducks/actions'
import { selectWhatsNewItems } from 'features/whatsNew/ducks/selectors'
import { isNewItem } from 'utils/isNewItem'
import { useTutorial } from 'features/tutorials/context/TutorialContext'
import { logoutUserRoutine } from 'features/auth/ducks/actions'
import AddIcon from '@mui/icons-material/Add'
import { ReactComponent as DashboardIcon } from 'assets/images/menu1.svg'
import { ReactComponent as ShortlistedIcon } from 'assets/images/manu3.svg'
import { ReactComponent as DraftsIcon } from 'assets/images/pencil.svg'
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined'
import { ReactComponent as TutorialsIcon } from 'assets/images/tutorial_v2.svg'
import { ReactComponent as LockIcon } from 'assets/images/lock-unlocked.svg'
import { ReactComponent as AnalyticsIcon } from 'assets/images/analytics.svg'
import { ReactComponent as WhatsNewIcon } from 'assets/images/whatsnew_2.svg'
import { ReactComponent as TasksIcon } from 'assets/images/tasks.svg'
import { ReactComponent as PortfolioIcon } from 'assets/images/portfolio.svg'
import PersonIcon from '@mui/icons-material/Person'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined'
import ReceiptIcon from '@mui/icons-material/Receipt'
import LogoutIcon from '@mui/icons-material/Logout'
import { ReactComponent as ContractorsSvg } from 'assets/images/contractors.svg'
import TutorialsPanel from 'features/tutorials/TutorialsPanel'
import { openHelp } from 'features/help/helpEvents'

const Navigation = ({ mobileOpen = false, setMobileOpen }) => {
  const dispatch = useDispatch()
  const { openTutorial } = useTutorial()

  const [tutorialsOpen, setTutorialsOpen] = useState(false)
  const [hasNewTutorials, setHasNewTutorials] = useState(false)
  const [hasNewWhatsNew, setHasNewWhatsNew] = useState(false)

  const location = useLocation()
  const params = useParams()
  const currentPath = location.pathname
  const currentUserRole = useSelector(getCurrentUserRole)
  const isOnline = useSelector(selectIsOnline)
  const hasAnyTaskManagementBp = useSelector(selectHasAnyTaskManagementBp)
  const featureVisibility = useSelector(selectFeatureVisibility)
  const featureOwner = useSelector(selectFeatureDefaultOwner)
  const opsNavVisible = isFeatureVisible(featureVisibility, 'tasks', featureOwner) || isFeatureVisible(featureVisibility, 'incidents', featureOwner)
  const checklistsNavVisible = isFeatureVisible(featureVisibility, 'checklists', featureOwner)
  const permitsNavVisible = isFeatureVisible(featureVisibility, 'permits', featureOwner)
  const handleLockedFeatureClick = () => {
    toast.info('Task Management isn’t enabled. Only a building’s owner can request it. Request it on a building you own, or contact your account holder to upgrade.')
  }
  const isFrsUser = currentUserRole === 'emergency_service'
  const isResponsiblePerson = currentUserRole === 'responsible_person'
  const isBpDetails = useMemo(() => {
    const bpBase = PRIVATE_PATHS.bpList
    const pathname = location?.pathname || ''
    try {
      const match = pathname.match(new RegExp(`^${bpBase}/([^/]+)(?:/([^/]+))?`))
      return Boolean(match?.[1])
    } catch (e) {
      const escaped = String(bpBase).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const match = pathname.match(new RegExp(`^${escaped}/([^/]+)(?:/([^/]+))?`))
      return Boolean(match?.[1])
    }
  }, [location.pathname])

  const tutorials = useSelector(selectTutorials) || []
  const rawWhatsNew = useSelector(selectWhatsNewItems)
  const whatsnew = Array.isArray(rawWhatsNew)
    ? rawWhatsNew
    : Array.isArray(rawWhatsNew?.data)
      ? rawWhatsNew.data
      : []

  useEffect(() => {
    if (checkIsOnline()) {
      deferIdle(() => {
        dispatch(fetchTutorialsRoutine())
        dispatch(getWhatsNewRoutine.trigger())
      })
    }
  }, [dispatch])

  useEffect(() => {
    if (setMobileOpen) setMobileOpen(false)
  }, [location.pathname, setMobileOpen])

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overscrollBehavior = 'none'
    } else {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.overscrollBehavior = ''
    }

    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.overscrollBehavior = ''
    }
  }, [mobileOpen])

  const checkBadges = useCallback(() => {
    const seenWhatsNewIds = JSON.parse(localStorage.getItem('seen_whatsnew_ids') || '[]')
    const hasUnseenWhatsNew = whatsnew.some((n) => {
      const id = n.id || n.uuid
      return isNewItem(n, 'whatsnew') && !seenWhatsNewIds.includes(id)
    })
    setHasNewWhatsNew(hasUnseenWhatsNew)

    const seenTutorialsIds = JSON.parse(localStorage.getItem('seen_tutorials_ids') || '[]')
    const hasUnseenTutorials = tutorials.some((t) => {
      const id = t.id || t.uuid
      return isNewItem(t, 'tutorials') && !seenTutorialsIds.includes(id)
    })
    setHasNewTutorials(hasUnseenTutorials)
  }, [whatsnew, tutorials])

  useEffect(() => {
    checkBadges()

    const handleStorageChange = () => checkBadges()
    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('focus', handleStorageChange)
    window.addEventListener('badges_updated', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('focus', handleStorageChange)
      window.removeEventListener('badges_updated', handleStorageChange)
    }
  }, [checkBadges])

  const handleOpenWhatsNew = () => {
    window.dispatchEvent(new Event('openWhatsNewPanel'))
    if (setMobileOpen) setMobileOpen(false)

    setTimeout(checkBadges, 500)
  }

  const handleLinkClick = () => {
    if (setMobileOpen) setMobileOpen(false)
  }

  const handleCreateNewBp = () => {
    if (setMobileOpen) setMobileOpen(false)
    if (window.location.pathname.includes('create-bp')) {
      window.location.reload()
    } else {
      redirect(PRIVATE_PATHS.createBp)
    }
  }

  return (
    <>
      <MobileBackdrop
        $isOpen={mobileOpen}
        $hasBottomBar={isBpDetails}
        onClick={() => setMobileOpen && setMobileOpen(false)}
      />

      <NavigationWrapper
        $isOpen={mobileOpen}
        $hasBottomBar={isBpDetails}
        onMouseEnter={() => {
          if (window.innerWidth > 768) {
            document.documentElement.style.setProperty('--nav-rail-width', '240px')
          }
        }}
        onMouseLeave={() => {
          document.documentElement.style.removeProperty('--nav-rail-width')
        }}
      >
        <ScrollContainer>
          <LinksContainer>
            {!isFrsUser && hasAnyTaskManagementBp && (
              <NarrowOnly>
                <IconLink
                  $isactive={currentPath.startsWith(PRIVATE_PATHS.calendar).toString()}
                  to={PRIVATE_PATHS.calendar}
                  onClick={handleLinkClick}
                >
                  <CalendarMonthOutlinedIcon aria-hidden='true' />
                  <div className="link-label">Calendar</div>
                </IconLink>
              </NarrowOnly>
            )}
            {!isFrsUser && !isResponsiblePerson && (
              <NarrowOnly>
                <IconLink
                  $isactive={currentPath.startsWith(PRIVATE_PATHS.cart).toString()}
                  to={PRIVATE_PATHS.cart}
                  onClick={handleLinkClick}
                >
                  <ShoppingBasketOutlinedIcon aria-hidden='true' />
                  <div className="link-label">Basket</div>
                </IconLink>
              </NarrowOnly>
            )}
            {isOnline && !isFrsUser && !isResponsiblePerson && (
              <IconLink
                $isactive={(currentPath.startsWith(PRIVATE_PATHS.dashboard)).toString()}
                to={PRIVATE_PATHS.dashboard}
                onClick={handleLinkClick}
              >
                <Tooltip title="Dashboard" placement="right">
                  <DashboardIcon />
                </Tooltip>
                <div className="link-label">Dashboard</div>
              </IconLink>
            )}

            {isOnline && (
              <IconLink
                $isactive={(
                  currentPath.startsWith(PRIVATE_PATHS.portfolios)
                  || currentPath.startsWith(PRIVATE_PATHS.bpList)
                ).toString()}
                to={PRIVATE_PATHS.portfolios}
                onClick={handleLinkClick}
              >
                <Tooltip title={isFrsUser ? 'Building Passports List' : 'Passports'} placement="right">
                  <PortfolioIcon />
                </Tooltip>
                <div className="link-label">
                  {isFrsUser ? 'Building Passports List' : 'Passports'}
                </div>
              </IconLink>
            )}

            {isOnline && (
              <IconLink
                $isactive={(currentPath.startsWith(PRIVATE_PATHS.savedPassports)).toString()}
                to={PRIVATE_PATHS.savedPassports}
                onClick={handleLinkClick}
              >
                <Tooltip title="Favourite Building Passports" placement="right">
                  <ShortlistedIcon />
                </Tooltip>
                <div className="link-label">Favourites</div>
              </IconLink>
            )}

            {!isFrsUser && (
              <>
                {isOnline && (
                  <IconLink
                    $isactive={(currentPath.startsWith(PRIVATE_PATHS.drafts)).toString()}
                    to={PRIVATE_PATHS.drafts}
                    onClick={handleLinkClick}
                  >
                    <Tooltip title="Drafts list" placement="right">
                      <DraftsIcon />
                    </Tooltip>
                    <div className="link-label">Drafts</div>
                  </IconLink>
                )}

                {isOnline && (
                  <IconLink
                    $isactive={(currentPath.startsWith(PRIVATE_PATHS.accessRequests)).toString()}
                    to={PRIVATE_PATHS.accessRequests}
                    onClick={handleLinkClick}
                  >
                    <Tooltip title="Request Access" placement="right">
                      <LockIcon />
                    </Tooltip>
                    <div className="link-label">Request Access</div>
                  </IconLink>
                )}

                {isOnline && checklistsNavVisible && (
                  <IconLink
                    $isactive={(currentPath.startsWith(PRIVATE_PATHS.analytics)).toString()}
                    to={PRIVATE_PATHS.analytics}
                    onClick={handleLinkClick}
                  >
                    <Tooltip title="Checklists Overview" placement="right">
                      <AnalyticsIcon />
                    </Tooltip>
                    <div className="link-label">Checklists Overview</div>
                  </IconLink>
                )}

                {/* Single hub entry — Tasks/Actions/Incidents now live as
                    tabs inside /tasks. The standalone /actions and
                    /incidents URLs still work as deep links. */}
                {hasAnyTaskManagementBp ? (
                  opsNavVisible ? (
                  <IconLink
                    $isactive={(
                      currentPath.startsWith(PRIVATE_PATHS.taskManagement)
                      || currentPath.startsWith(PRIVATE_PATHS.actions)
                      || currentPath.startsWith(PRIVATE_PATHS.incidents)
                    ).toString()}
                    to={PRIVATE_PATHS.taskManagement}
                    onClick={handleLinkClick}
                  >
                    <Tooltip title="Tasks & Remedials" placement="right">
                      <TasksIcon />
                    </Tooltip>
                    <div className="link-label">Tasks &amp; Remedials</div>
                  </IconLink>
                  ) : null
                ) : (
                  <IconLink as="div" role="button" tabIndex={0} $isactive="false" $locked onClick={handleLockedFeatureClick}>
                    <Tooltip title="Tasks & Remedials. Not enabled. Contact your account holder to upgrade." placement="right">
                      <LockedIconWrap>
                        <TasksIcon />
                        <LockBadge><LockRoundedIcon style={{ fontSize: 10, width: 10, height: 10 }} /></LockBadge>
                      </LockedIconWrap>
                    </Tooltip>
                    <div className="link-label">Tasks &amp; Remedials</div>
                  </IconLink>
                )}

                {isOnline && (hasAnyTaskManagementBp ? (
                  permitsNavVisible ? (
                  <IconLink
                    $isactive={(
                      currentPath.startsWith(PRIVATE_PATHS.contractManagement)
                      || currentPath.startsWith(PRIVATE_PATHS.contractors)
                    ).toString()}
                    to={PRIVATE_PATHS.contractManagement}
                    onClick={handleLinkClick}
                  >
                    <Tooltip title="Contract Management" placement="right">
                      <ContractorsIcon />
                    </Tooltip>
                    <div className="link-label">Contracts</div>
                  </IconLink>
                  ) : null
                ) : (
                  <IconLink as="div" role="button" tabIndex={0} $isactive="false" $locked onClick={handleLockedFeatureClick}>
                    <Tooltip title="Contract Management. Not enabled. Contact your account holder to upgrade." placement="right">
                      <LockedIconWrap>
                        <ContractorsIcon />
                        <LockBadge><LockRoundedIcon style={{ fontSize: 10, width: 10, height: 10 }} /></LockBadge>
                      </LockedIconWrap>
                    </Tooltip>
                    <div className="link-label">Contracts</div>
                  </IconLink>
                ))}
              </>
            )}
          </LinksContainer>

          <BottomLinks>
            {isOnline && !isFrsUser && !isResponsiblePerson && (
              <>
                <CreateBpLink onClick={handleCreateNewBp}>
                  <Tooltip title="Create Building Passport" placement="right">
                    <CreateBpIconWrapper>
                      <AddIcon />
                    </CreateBpIconWrapper>
                  </Tooltip>
                  <div className="link-label">Create Building Passport</div>
                </CreateBpLink>

                <AccountLink
                  $isactive={(currentPath.startsWith(PRIVATE_PATHS.billingList)).toString()}
                  to={PRIVATE_PATHS.billingList}
                  onClick={handleLinkClick}
                >
                  <Tooltip title="Billing list" placement="right">
                    <BillingIcon />
                  </Tooltip>
                  <div className="link-label">Billing list</div>
                </AccountLink>
              </>
            )}

            {isOnline && (
              <AccountLink
                $isactive={(currentPath.startsWith(PRIVATE_PATHS.profileSettings)).toString()}
                to={PRIVATE_PATHS.profileSettings}
                onClick={handleLinkClick}
              >
                <Tooltip title="My profile" placement="right">
                  <ProfileIcon />
                </Tooltip>
                <div className="link-label">My profile</div>
              </AccountLink>
            )}

            <LogoutButton onClick={() => {
              dispatch(logoutUserRoutine())
              if (setMobileOpen) setMobileOpen(false)
            }}>
              <LogoutIconStyled />
              <div className="link-label">Logout</div>
            </LogoutButton>

            {isOnline && (
              <>
                <WhatsNewButton onClick={handleOpenWhatsNew}>
                  <WhatsNewIcon />
                  {hasNewWhatsNew && <PulseBadge />}
                  <div className="link-label">What's New</div>
                </WhatsNewButton>

                <TutorialsButton onClick={() => {
                  setTutorialsOpen(true)
                  if (setMobileOpen) setMobileOpen(false)
                }}>
                  <TutorialsIcon />
                  {hasNewTutorials && <PulseBadge />}
                  <div className="link-label">Tutorials</div>
                </TutorialsButton>

                <HelpLink as='button' type='button' onClick={() => {
                  openHelp()
                  setMobileOpen?.(false)
                }}>
                  <ExploreOutlinedIcon />
                  <div className='link-label'>Guide me</div>
                </HelpLink>
              </>
            )}
          </BottomLinks>
        </ScrollContainer>
      </NavigationWrapper>

      {tutorialsOpen && (
        <>
          <PanelBackdrop onClick={() => {
            setTutorialsOpen(false)
            checkBadges()
          }} />
          <PanelWrapper>
            <TutorialsPanel
              onClose={() => {
                setTutorialsOpen(false)
                checkBadges()
              }}
              onSelectTutorial={(tutorial) => {
                openTutorial(tutorial)
                setTutorialsOpen(false)
                checkBadges()
              }}
            />
          </PanelWrapper>
        </>
      )}
    </>
  )
}

export default Navigation

const MobileBackdrop = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: ${({ $hasBottomBar }) =>
      $hasBottomBar
        ? 'calc(64px + env(safe-area-inset-bottom))'
        : 'env(safe-area-inset-bottom)'};
    background: rgba(0,0,0,0.5);
    z-index: 1199;
    backdrop-filter: blur(2px);
    touch-action: none;
  }
`

const NavigationWrapper = styled.div`
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.navigation};
  position: fixed;
  z-index: 50;
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), width 0.18s ease, box-shadow 0.18s ease;
  overflow: hidden;
  border-right: 2px solid ${({ theme }) => theme.colors.lightGrey};
  top: 0;
  left: 0;
  height: 100vh;
  width: ${({ theme }) => theme.dimensions.navWidth};
  display: flex;
  flex-direction: column;

  &:hover {
    width: 240px;
    box-shadow: 6px 0 24px -4px rgba(15, 23, 42, 0.16);

    .link-label {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .link-label {
    opacity: 0;
    font-size: 15px;
    font-weight: 500;
    left: 70px;
    position: absolute;
    white-space: nowrap;
    transition: opacity 0.2s 0.1s, transform 0.2s 0.1s;
    transform: translateX(-10px);
  }

  @media (max-width: 768px) {
    z-index: 1200;
    width: 100%;
    height: auto;
    max-height: 75vh;
    top: auto;
    left: 0;
    border-right: none;
    bottom: ${({ $hasBottomBar }) =>
      $hasBottomBar
        ? 'calc(64px + env(safe-area-inset-bottom))'
        : 'env(safe-area-inset-bottom)'};
    border-top: 1px solid rgba(0,0,0,0.08);
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    box-shadow: 0 -5px 25px rgba(0,0,0,0.15);
    transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(120%)'};
    overscroll-behavior: contain;

    .link-label {
      opacity: 1;
      position: static;
      transform: none;
      margin-left: 16px;
    }

    &:hover {
      width: 100%;
    }
  }
`

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  justify-content: space-between;
  padding-bottom: 20px;

  -webkit-overflow-scrolling: touch;
`

const LinksContainer = styled.div`
  width: 100%;
  margin-top: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    margin-top: 24px;
    align-items: flex-start;
  }
`

const BottomLinks = styled.div`
  @media (max-width: 768px) {
    border-top: 1px solid rgba(0,0,0,0.05);
    padding-top: 12px;
    margin-top: 12px;
  }
`

const IconLink = styled(Link)`
  box-sizing: border-box;
  padding-left: 35px;
  position: relative;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  color: ${({ theme }) => theme.colors.haiti};
  text-decoration: none;

  svg {
    width: 24px;
    flex-shrink: 0;
  }

  ${({ $locked }) => $locked && `
    opacity: 0.5;
    svg { filter: grayscale(1); }
  `}

  &::before {
    content: '';
    display: ${({ $isactive }) => ($isactive === 'true' ? 'block' : 'none')};
    background-color: ${({ theme }) => theme.colors.primary};
    left: 0;
    position: absolute;
    height: 100%;
    width: 4px;
    border-radius: 0 4px 4px 0;
    box-shadow: 0 2px 8px rgba(209, 73, 37, 0.4);
  }

  @media (max-width: 768px) {
    padding-left: 24px;
    width: 100%;
    background-color: ${({ $isactive, theme }) =>
      $isactive === 'true' ? theme.colors.grey[100] : 'transparent'};
  }
`

const TutorialsButton = styled.div`
  box-sizing: border-box;
  padding-left: 35px;
  position: relative;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
  height: 44px;
  color: ${({ theme }) => theme.colors.haiti};

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding-left: 24px;
  }
`

const WhatsNewButton = styled(TutorialsButton)``

const AccountLink = styled(IconLink)`
  display: none;

  @media (max-width: 768px) {
    display: flex;
  }
`

const LogoutButton = styled(TutorialsButton)`
  display: none;

  @media (max-width: 768px) {
    display: flex;
  }
`

const ProfileIcon = styled(PersonIcon)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`

const BillingIcon = styled(ReceiptIcon)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`

const LockedIconWrap = styled.div`
  position: relative;
  display: inline-flex;
`

const LockBadge = styled.span`
  position: absolute;
  right: -6px;
  bottom: -4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #b45309;
  color: #fff;
`

const ContractorsIcon = styled(ContractorsSvg)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`

const LogoutIconStyled = styled(LogoutIcon)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`

const CreateBpLink = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    box-sizing: border-box;
    padding-left: 24px;
    position: relative;
    margin-bottom: 12px;
    align-items: center;
    cursor: pointer;
    width: 100%;
    height: 44px;
    color: ${({ theme }) => theme.colors.haiti};
    text-decoration: none;

    svg {
      width: 24px;
      flex-shrink: 0;
    }
  }
`

const CreateBpIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;

  svg {
    font-size: 18px;
  }
`

const HelpLink = styled.a`
  box-sizing: border-box;
  padding: 0 0 0 35px;
  border: none;
  background: transparent;
  font: inherit;
  text-align: left;
  position: relative;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    padding-left: 24px;
    margin-bottom: 10px;
  }
`

export const PulseBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 67px;
  width: 8px;
  height: 8px;
  background: #ff4d4f;
  border-radius: 50%;
  box-shadow: 0 0 0 rgba(255, 77, 79, 0.4);
  animation: pulse 1.6s infinite;
  pointer-events: none;
  z-index: 10;
  transition: left 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), top 0.3s;
  ${NavigationWrapper}:hover & {
    left: 210px;
    top: 18px;
  }

  @media (max-width: 768px) {
    left: auto;
    right: 20px;
    top: 18px;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.9);
      box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.4);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 6px rgba(255, 77, 79, 0);
    }
    100% {
      transform: scale(0.9);
      box-shadow: 0 0 0 0 rgba(255, 77, 79, 0);
    }
  }
`

const PanelWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 80px;
  height: 100vh;
  width: 400px;
  max-width: calc(100vw - 80px);
  z-index: 9999;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
  box-shadow: 8px 0 20px rgba(0, 0, 0, 0.08);
  animation: slideIn 0.35s ease forwards;

  @media (max-width: 768px) {
    left: 0;
    width: 100%;
    max-width: 100%;
    border-radius: 0;
  }
`

const PanelBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 18, 24, 0.25);
  backdrop-filter: blur(1.5px);
  z-index: 9998;
  animation: fadeIn 0.25s ease;
`

const NarrowOnly = styled.div`
  display: none;

  @media (max-width: 419px) {
    display: block;
  }
`
